
import { SUPABASE_PUBLISHABLE_KEY } from './config';
import { buildRestApiUrl, getStandardHeaders } from './urlBuilder';
import { supabase } from './client';
import { Database } from './types';

// Определяем тип для имен таблиц на основе типа Database
type TableName = keyof Database['public']['Tables'] | keyof Database['public']['Views'];

// Проверяем существование таблицы с использованием URL builder
export const checkTableExists = async (tableName: string): Promise<boolean> => {
  try {
    const url = buildRestApiUrl(`${tableName}?limit=1`);
    console.log(`Проверка существования таблицы ${tableName} по URL ${url}`);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...getStandardHeaders(),
        'Prefer': 'count=exact'
      }
    });
    
    console.log(`Статус ответа при проверке таблицы ${tableName}: ${response.status}`);
    return response.status !== 404;
  } catch (error) {
    console.error(`Ошибка при проверке таблицы ${tableName}:`, error);
    return false;
  }
};

// Проверка доступности таблицы
export const checkTableAccess = async (tableName: string): Promise<{ success: boolean; data?: any; error?: any; message?: string }> => {
  try {
    // Сначала проверяем существование таблицы через REST API
    const tableExists = await checkTableExists(tableName);
    
    if (!tableExists) {
      return { 
        success: false, 
        error: { message: `Таблица ${tableName} не существует` }, 
        message: `Таблица ${tableName} не найдена. Выполните SQL скрипт инициализации.`
      };
    }
    
    // Если таблица существует, проверяем права доступа через REST API
    try {
      const url = buildRestApiUrl(`${tableName}?count=exact&limit=0`);
      const response = await fetch(url, {
        method: 'HEAD',
        headers: getStandardHeaders()
      });
      
      if (!response.ok) {
        return { 
          success: false, 
          error: { status: response.status, message: response.statusText }, 
          message: `Нет доступа к таблице ${tableName}: ${response.status} ${response.statusText}`
        };
      }
      
      return { 
        success: true, 
        data: { count: parseInt(response.headers.get('content-range')?.split('/')[1] || '0') }, 
        message: `Доступ к таблице ${tableName} разрешен` 
      };
    } catch (error: any) {
      return { 
        success: false, 
        error, 
        message: `Ошибка при проверке доступа к таблице ${tableName}: ${error.message}`
      };
    }
  } catch (error: any) {
    return { 
      success: false, 
      error, 
      message: `Ошибка при проверке доступа к таблице ${tableName}: ${error.message}`
    };
  }
};

// Интерфейс для параметров запроса
interface QueryOptions {
  select?: string;
  filter?: string;
  order?: string;
  limit?: number;
  offset?: number;
  singular?: boolean;
}

// Получить данные из таблицы с обработкой ошибок
export const fetchTableData = async (tableName: string, options: QueryOptions = {}) => {
  try {
    // Проверка существования таблицы
    const tableExists = await checkTableExists(tableName);
    
    if (!tableExists) {
      return { 
        success: false, 
        error: { code: 'PGRST116', message: `Таблица ${tableName} не существует` }, 
        message: `Таблица ${tableName} не найдена. Выполните SQL скрипт инициализации.`
      };
    }
    
    // Если таблица существует, используем REST API вместо клиента supabase
    console.log(`Выполняем запрос к таблице ${tableName} через REST API`);
    try {
      // Формируем URL запроса с параметрами
      const select = options.select || '*';
      let url = buildRestApiUrl(`${tableName}?select=${encodeURIComponent(select)}`);
      
      // Добавляем остальные параметры, если они заданы
      if (options.filter) {
        url += `&${options.filter}`;
      }
      
      if (options.order) {
        url += `&order=${encodeURIComponent(options.order)}`;
      }
      
      if (options.limit) {
        url += `&limit=${options.limit}`;
      }
      
      if (options.offset) {
        url += `&offset=${options.offset}`;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getStandardHeaders()
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Ошибка получения данных из ${tableName}:`, response.status, errorText);
        return { 
          success: false, 
          error: { status: response.status, message: errorText }, 
          message: `Ошибка при получении данных из таблицы ${tableName}: ${response.status} ${response.statusText}`
        };
      }
      
      const data = await response.json();
      return { 
        success: true, 
        data, 
        message: `Данные из таблицы ${tableName} успешно получены` 
      };
    } catch (error: any) {
      console.error(`Ошибка при запросе к таблице ${tableName}:`, error);
      return { 
        success: false, 
        error, 
        message: `Ошибка при загрузке данных из таблицы ${tableName}: ${error.message}`
      };
    }
  } catch (error: any) {
    console.error(`Критическая ошибка при загрузке ${tableName}:`, error);
    return { 
      success: false, 
      error, 
      message: `Критическая ошибка при загрузке данных из таблицы ${tableName}: ${error.message}` 
    };
  }
};
